import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { CheckCheck, LoaderCircle, PhoneCall } from 'lucide-react';
import { cn } from '@/lib/utils';
import { displayName } from '@/lib/conversationFormat';
import { ticketPhone } from '@/lib/callbackTicket';
import { RESOLVE_NOTE_MAX, type CallbackTicket } from '@/services/callbackTicketService';
import { CharCounter } from '@/components/ui/char-counter';
import { SinceText } from './TimeText';
import { Avatar, joinParts } from './ListRowParts';

export type ResolveTicket = (ticket: CallbackTicket, note: string) => Promise<void>;

type Props = {
  ticket: CallbackTicket;
  selected: boolean;
  /** Opens the ticket's call (stored transcript, or the call by its sid). */
  onSelect: () => void;
  /** Marks the ticket done; rejects when that failed (the caller tells the user). */
  onResolve?: ResolveTicket;
  /** „über …“ when the salon runs several agent lines. */
  agent?: string | null;
};

const actionClass = cn(
  'inline-flex h-7 items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 text-xs font-semibold text-foreground transition-all',
  'hover:border-foreground/25 hover:bg-accent active:scale-[0.98]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-card',
);

/**
 * A Rückruf-Ticket of the phone agent under „Wartet auf Rückmeldung“: who
 * wants a call back, why, when it suits them and where the agent got stuck.
 * The row opens the call; „Anrufen“ dials, „Erledigt“ closes the ticket with
 * an optional note. The actions sit next to the row button, never inside it.
 */
export function CallbackTicketRow({ ticket, selected, onSelect, onResolve, agent }: Props) {
  const formId = useId();
  const noteId = useId();
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const doneRef = useRef<HTMLButtonElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // Set here too: StrictMode runs the cleanup once right after mounting.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (resolving) noteRef.current?.focus();
  }, [resolving]);

  const phone = ticketPhone(ticket);
  const name = ticket.customer_name?.trim() || null;
  const title = displayName(name, ticket.customer_phone);
  const wish = ticket.preferred_window ? `Wunsch: ${ticket.preferred_window}` : null;

  const close = () => {
    if (busy) return;
    setResolving(false);
    // Back to where the person was — the row stays, only the form goes.
    doneRef.current?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !onResolve) return;
    setBusy(true);
    try {
      await onResolve(ticket, note.trim());
      if (!mountedRef.current) return;
      setResolving(false);
      setNote('');
    } catch {
      // The caller showed what went wrong; the note stays for another try.
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const onFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-xl transition-colors',
        selected ? 'bg-brand-soft ring-1 ring-inset ring-brand/35' : 'hover:bg-accent/70',
      )}
    >
      <button
        type="button"
        data-row
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className="flex w-full items-start gap-3 rounded-xl px-2.5 pb-1.5 pt-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-card"
      >
        <Avatar name={name} phone={ticket.customer_phone} channel="phone" />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[13.5px] font-semibold text-foreground">
              <span className="sr-only">Rückruf per Telefon: </span>
              {title}
            </span>
            <span className="flex-shrink-0 text-[11.5px] font-semibold tabular-nums text-brand-strong">
              <SinceText since={ticket.created_at} />
            </span>
          </span>
          <span className="mt-0.5 line-clamp-2 break-words text-[12.5px] leading-snug text-foreground/85">
            {ticket.reason}
          </span>
          <span className="block truncate text-[11.5px] leading-snug text-muted-foreground">
            {joinParts([wish, name ? phone : null, ticket.site_name, agent])}
          </span>
          {ticket.stuck_note && (
            <span className="block truncate text-[11.5px] leading-snug text-muted-foreground" title={ticket.stuck_note}>
              Agent: {ticket.stuck_note}
            </span>
          )}
        </span>
      </button>

      <div className="flex flex-wrap items-center gap-1.5 pb-2 pl-[3.875rem] pr-2.5">
        {phone && (
          <a href={`tel:${phone}`} aria-label={`Anrufen: ${name ? `${name}, ` : ''}${phone}`} className={actionClass}>
            <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
            Anrufen
          </a>
        )}
        {onResolve && (
          <button
            ref={doneRef}
            type="button"
            aria-label={`Erledigt: ${title}`}
            aria-expanded={resolving}
            aria-controls={resolving ? formId : undefined}
            onClick={() => (resolving ? close() : setResolving(true))}
            className={actionClass}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Erledigt
          </button>
        )}
      </div>

      {resolving && onResolve && (
        <form
          id={formId}
          onSubmit={submit}
          onKeyDown={onFormKeyDown}
          aria-label={`Rückruf von ${title} als erledigt markieren`}
          className="space-y-1.5 px-2.5 pb-2.5"
        >
          <label htmlFor={noteId} className="block text-[11.5px] font-medium text-muted-foreground">
            Notiz (optional)
          </label>
          <textarea
            ref={noteRef}
            id={noteId}
            value={note}
            rows={2}
            maxLength={RESOLVE_NOTE_MAX}
            placeholder="z. B. Zurückgerufen, Termin eingetragen"
            onChange={(e) => setNote(e.target.value)}
            className="block w-full resize-none rounded-lg border border-border bg-background px-2.5 py-1.5 text-[12.5px] leading-snug text-foreground placeholder:text-muted-foreground focus-visible:border-brand/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
          />
          {note.length > RESOLVE_NOTE_MAX * 0.8 && <CharCounter value={note} max={RESOLVE_NOTE_MAX} />}
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={close}
              aria-disabled={busy}
              className="inline-flex h-7 items-center whitespace-nowrap rounded-lg px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand aria-disabled:pointer-events-none aria-disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              aria-busy={busy}
              aria-disabled={busy}
              className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg bg-brand px-2.5 text-xs font-semibold text-brand-foreground shadow-sm transition-all hover:bg-brand-hover active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 focus-visible:ring-offset-card aria-disabled:cursor-wait aria-disabled:opacity-70"
            >
              {busy && <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              Als erledigt markieren
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CallbackTicketRow;
